import { ReactElement, useEffect, useMemo, useState } from "react";
import { Calendar, Leader } from "../../../store";
import { CabModal } from "@CabComponents/CabModal";
import { CabButton } from "@CabComponents/CabButton";
import { Box, FormLabel, Typography, styled } from "@mui/material";
import CabAvatar from "@CabComponents/CabAvatar";
import { getCalendarDisplayNamesById } from "../../../utils/scheduleUtils";
import { CabAutocomplete } from "@CabComponents/CabAutocomplete";
import { CabSwitch } from "@CabComponents/CabSwitch";
import { uniqBy } from "lodash-es";
import { CirclePicker } from "react-color";
import { GOOGLE_COLORS, MICROSOFT_COLORS, PROVIDER } from "../../../constants";
import { CabToggleChip } from "@CabComponents/CabToggleChip";
import { AnalyticsEventColor } from "../../../store/cabinetApi/generated/analytics";
import AnalyticsEventColorPicker from "../../Pickers/AnalyticsEventColorPicker";
import colors from "../../../colors";
import { MissingCalendars } from "../MissingCalendars";
import { CalendarAccessIndicators } from "../../CalendarAccessIndicators";


export interface AddEditCalendarModalProps {
  open: boolean;
  calendars: Calendar[];
  leader: Leader;
  onClose: () => void;
  calendarId?: number;
  onAddEditCalendar: (
    calendarId: number, 
    conflicts: boolean, 
    analytics: boolean, 
    crm: boolean, 
    color: string, 
    defaultHoldColor: AnalyticsEventColor | null
  ) => void;
  hasCrmFeature?: boolean;
  organizationId: number;
  handleSetAdditionalCalendars?: (value: { [eml: string]: Calendar[] }) => void;
  additionalCalendars?: { [eml: string]: Calendar[] };
  onGetEmailCalendars?: (email: string) => Promise<Calendar[]>;
  additionalDelegateEmails?: (string | null)[];
  loading?: boolean;
}

export const AddEditCalendarModal = ({
  open, calendars, calendarId, leader, onClose, onAddEditCalendar, hasCrmFeature, organizationId,
  handleSetAdditionalCalendars, additionalCalendars = {}, onGetEmailCalendars, additionalDelegateEmails = [],
  loading = false
}: AddEditCalendarModalProps): ReactElement => {
  const [calId, setCalId] = useState(calendarId);
  const [calendarColorPickerOpen, setCalendarColorPickerOpen] = useState(false);
  const [defaultHoldColorPickerOpen, setDefaultHoldColorPickerOpen] = useState(false);

  const calendar = useMemo(
    () => calendars.find((cal) => cal.id === calId ),
    [calendars, calId]
  );

  const [checkConflicts, setCheckConflicts] = useState<boolean>(calId && 
    leader.leader_calendars?.[calId]?.prevent_conflict !== undefined ? 
    leader.leader_calendars?.[calId].prevent_conflict : true);
  const [includeAnalytics, setIncludeAnalytics] = useState<boolean>(calId && 
    leader.leader_calendars?.[calId]?.allow_calendar_analytics !== undefined ? 
    leader.leader_calendars?.[calId].allow_calendar_analytics : true);
  const [allowCrm, setAllowCrm] = useState<boolean>(calId && 
    leader.leader_calendars?.[calId]?.allow_crm !== undefined ? 
    leader.leader_calendars?.[calId].allow_crm : true);
  
  const [calendarColor, setCalendarColor] = useState<string>(calendar ? 
    (calendar.overrideBackgroundColor || calendar.autoBackgroundColor) : "#FF0000");

  const [defaultHoldColor, setDefaultHoldColor] = useState<AnalyticsEventColor | null>(calendar
    ? calendar.defaultHoldColor || null : null
  );

  const calendarDisplayNamesById = getCalendarDisplayNamesById(calendars);
  const calendarOptions = calendars.map((calendarItr) => ({
    value: calendarItr.id,
    icon: <CalendarAccessIndicators calendar={calendarItr} reserveColumns />,
    label: calendarDisplayNamesById[calendarItr.id],
    color: calendarItr.backgroundColor,
  }));

  const uniqueCalendarOptions = uniqBy(calendarOptions, cal => cal.value)
    .sort((a, b) => { // Sort alphabetically
      const calA = a.label.toUpperCase();
      const calB = b.label.toUpperCase();
      return calA < calB ? -1 : calA > calB ? 1 : 0;
    });

  useEffect(() => {
    setCheckConflicts(calId && leader.leader_calendars?.[calId]?.prevent_conflict !== undefined 
      ? leader.leader_calendars?.[calId].prevent_conflict : true);
    setIncludeAnalytics(calId && leader.leader_calendars?.[calId]?.allow_calendar_analytics !== undefined
      ? leader.leader_calendars?.[calId].allow_calendar_analytics : true);
    setAllowCrm(calId && leader.leader_calendars?.[calId]?.allow_crm !== undefined
      ? leader.leader_calendars?.[calId].allow_crm : true);
    setCalendarColor(calendar ? 
      (calendar.overrideBackgroundColor || calendar.autoBackgroundColor) : "#FF0000");
  }, [calendar, calId, leader.leader_calendars]);

  const handleCalendarColor = (color: string) => {
    setCalendarColor(color);
    setCalendarColorPickerOpen(false);
  };

  const handleSetDefaultHoldColor = (color: AnalyticsEventColor | null) => {
    setDefaultHoldColor(color);
    setDefaultHoldColorPickerOpen(false);
  };

  const colorSet = useMemo(() => {
    if (calendar?.provider === PROVIDER["GOOGLE"].id) {
      return Object.values(GOOGLE_COLORS["calendar"]).map(color => color.background);
    } else if (calendar?.provider === PROVIDER["MICROSOFT"].id) {
      return Array.from(Object.values(MICROSOFT_COLORS));
    }
  }, [calendar]);

  return (
    <>
      <CabModal
        open={open}
        title={calendarId ? 'Edit Calendar' : 'Add Calendar'}
        onClose={onClose}
        closeIcon
        sx={{
          '& .MuiPaper-root': {
            width: 'min(570px, calc(100vw - 32px))',
            minWidth: 0,
          },
        }}
        actionButtons={            
          <Box
            sx={{
              display: "flex",
              gap: 1
            }}>
            <CabButton buttonType='secondary' onClick={onClose}>
              Cancel
            </CabButton>
            <CabButton onClick={calId ? () => onAddEditCalendar(
              calId, checkConflicts, includeAnalytics, allowCrm, calendarColor, defaultHoldColor
            ) : undefined} 
            disabled={!calId}
            >
              {calendarId ? 'Save Changes' : 'Add Calendar'}
            </CabButton>
          </Box>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 0
          }}>
          <Typography variant="body1" sx={{
            fontWeight: 700
          }}>Executive Calendar</Typography>
          <Box
            sx={{
              display: 'flex',
              minWidth: 0,
              alignItems: "center"
            }}>
            <CabAvatar
              color={leader.color}
              size="small"
              src={leader.pic_url}
              name={`${leader.first_name} ${leader.last_name}`}
              sx={{ marginRight: 1 }}
            />
            <Typography variant="h5" sx={{ overflowWrap: 'anywhere' }}>
              {`${leader.first_name} ${leader.last_name}`}
            </Typography>
          </Box>
          <CabAutocomplete<number, never>
            value={calId  || null}
            onChange={(v) => setCalId(Number(v))}
            placeholder={calId ? undefined : 'Select a calendar'}
            sx={{ width: '100%', minWidth: 0 }}
            options={uniqueCalendarOptions}
            optionIconPlacement="end"
          />
          {!calendarId && handleSetAdditionalCalendars && onGetEmailCalendars && (
            <MissingCalendars
              variant="inlineLink"
              handleSetAdditionalCalendars={handleSetAdditionalCalendars}
              additionalCalendars={additionalCalendars}
              onGetEmailCalendars={onGetEmailCalendars}
              additionalDelegateEmails={additionalDelegateEmails}
              loading={loading}
            />
          )}
          <Typography variant="body1" sx={{
            fontWeight: 700
          }}>Calendar Settings</Typography>
          <Box>
            <AdditionalOption>
              <FormLabel>Check calendar for conflicts</FormLabel>
              <CabSwitch checked={checkConflicts} onChange={() => setCheckConflicts(!checkConflicts)} />
            </AdditionalOption>
            <AdditionalOption>
              <FormLabel>Include calendar in analytics</FormLabel>
              <CabSwitch checked={includeAnalytics} onChange={() => setIncludeAnalytics(!includeAnalytics)} />
            </AdditionalOption>
            {hasCrmFeature &&
              <AdditionalOption>
                <FormLabel>Collect contacts from events </FormLabel>
                <CabSwitch checked={allowCrm} onChange={() => setAllowCrm(!allowCrm)} />
              </AdditionalOption>
            }
            <AdditionalOption>
              <FormLabel>Cabinet calendar color</FormLabel>

              {calendarColor && <Box sx={{padding: 0.5}}><CabToggleChip
                chipColor={calendarColor}
                selected={true}
                sx={{
                  width: 40,
                  fontStyle: "italic",
                  cursor: "pointer"
                }}
                onClick={() => setCalendarColorPickerOpen(true)}
              /></Box>}
            </AdditionalOption>
            <AdditionalOption>
              <FormLabel>{
                `Default Hold color${calendar?.provider === PROVIDER["MICROSOFT"].id ? "/category" : ""}`
              }</FormLabel>

              <Box sx={{padding: 0.5}}>
                <CabToggleChip
                  chipColor={defaultHoldColor?.color || colors.white900}
                  label={calendar?.provider === PROVIDER["MICROSOFT"].id 
                    ? defaultHoldColor?.category || "No Category Selected" 
                    : defaultHoldColor?.color ? "" : "Default Calendar Color"
                  }
                  selected={true}
                  sx={{
                    border: `1px solid ${defaultHoldColor?.color || colors.black300}`,
                    minWidth: 40,
                    cursor: "pointer"
                  }}
                  onClick={() => setDefaultHoldColorPickerOpen(true)}
                  translateColor
                />
              </Box>
            </AdditionalOption>
          </Box>
        </Box>
      </CabModal>
      <CabModal
        open={calendarColorPickerOpen}
        actionButtons={<></>}
        onClose={() => setCalendarColorPickerOpen(false)}
        closeIcon
        title="Choose Calendar Color"
      >
        <CirclePicker 
          width='100%'
          circleSpacing={8}
          circleSize={32}
          colors={colorSet} 
          color={calendarColor}
          onChange={(color): void => handleCalendarColor(color.hex)}
        />
      </CabModal>
      <AnalyticsEventColorPicker
        show={defaultHoldColorPickerOpen}
        onSelect={handleSetDefaultHoldColor}
        onCancel={() => setDefaultHoldColorPickerOpen(false)}
        calendar={calendar}
      />
    </>
  );
};

const AdditionalOption = styled(FormLabel, { label: 'AdditionalOption' })(() => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'space-between',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
}));
